import { create } from "zustand";

import type {
  DraftMeta,
  PersistedMedia,
  SerializedEditorStateJson,
} from "../lib/storage/index.js";

export type DraftsScreen = "editor" | "drafts" | "help";

interface DraftsState {
  /** id of the draft we're currently editing, null if no active draft yet. */
  activeDraftId: string | null;
  /** Bumped each time we want to remount the editor with a fresh state. */
  loadedRevision: number;
  /** Initial JSON for the next editor mount; consumed once. */
  initialState: SerializedEditorStateJson | null;
  /**
   * Media to hydrate the slot with when the next draft loads. `null`
   * (vs. `undefined`) is meaningful — it means "explicitly clear the
   * slot", used when opening a draft that has no attached media to
   * prevent the previous draft's media from leaking.
   */
  initialMedia: PersistedMedia | null;
  list: DraftMeta[];
  loadingList: boolean;
  screen: DraftsScreen;

  setScreen: (screen: DraftsScreen) => void;
  setList: (list: DraftMeta[]) => void;
  setLoadingList: (v: boolean) => void;
  /**
   * Quiet setter — only changes activeDraftId, does NOT queue a state
   * reload. Used by autosave to promote an unnamed buffer to a tracked
   * draft on first content.
   */
  setActiveDraftId: (id: string | null) => void;
  /** Load a draft into the editor. */
  openDraft: (
    id: string | null,
    state: SerializedEditorStateJson | null,
    media?: PersistedMedia | null,
  ) => void;
  /** Mark the loaded state as consumed by the editor. */
  consumeInitialState: () => void;
  /** Update meta after autosave succeeds. */
  upsertMeta: (meta: DraftMeta) => void;
  removeMeta: (id: string) => void;
}

export const useDraftsStore = create<DraftsState>((set) => ({
  activeDraftId: null,
  loadedRevision: 0,
  initialState: null,
  initialMedia: null,
  list: [],
  loadingList: false,
  screen: "editor",

  setScreen: (screen) => {
    set({ screen });
  },
  setList: (list) => {
    set({ list });
  },
  setLoadingList: (loadingList) => {
    set({ loadingList });
  },
  setActiveDraftId: (id) => {
    set({ activeDraftId: id });
  },
  openDraft: (id, state, media = null) => {
    set((s) => ({
      activeDraftId: id,
      initialState: state,
      initialMedia: media ?? null,
      loadedRevision: s.loadedRevision + 1,
      screen: "editor",
    }));
  },
  consumeInitialState: () => {
    set({ initialState: null, initialMedia: null });
  },
  upsertMeta: (meta) => {
    set((s) => {
      const without = s.list.filter((d) => d.id !== meta.id);
      return { list: [meta, ...without] };
    });
  },
  removeMeta: (id) => {
    set((s) => ({ list: s.list.filter((d) => d.id !== id) }));
  },
}));
